// Practice history: newest first, click a row for the scorecard; unscored sessions can be scored from here.
import { useState } from 'preact/hooks';
import type { PracticeFinishResponse, PracticeSessionRow } from '../../../src/shared/api-ai';
import { api, errorMessage } from '../api';
import { Empty, ErrorBanner, fmtDateTime } from '../components/ui';
import { KIND_LABEL } from './labels';
import { ScorecardView } from './ScorecardView';

function dur(sec: number | null): string {
  if (sec === null) return '—';
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

export function History(props: { sessions: PracticeSessionRow[]; showUser?: boolean; onUpdated: (s: PracticeSessionRow) => void }) {
  const [open, setOpen] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (props.sessions.length === 0) return <Empty>No practice calls yet.</Empty>;

  const score = async (s: PracticeSessionRow) => {
    setBusy(s.id); setError(null);
    try {
      const r = await api.post<PracticeFinishResponse>(`/api/training/practice/${s.id}/finish`, {});
      props.onUpdated(r.session);
      if (r.message) setError(r.message);
      setOpen(s.id);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div class="stack">
      <ErrorBanner error={error} />
      <table>
        <thead>
          <tr>
            <th>When</th>{props.showUser ? <th>Setter</th> : null}<th>Campaign</th><th>Prospect</th><th>Length</th><th>Score</th><th />
          </tr>
        </thead>
        <tbody>
          {props.sessions.map((s) => {
            const canScore = !s.scorecard && !s.endedAt && Date.now() - new Date(s.startedAt).getTime() > 60_000;
            return [
              <tr class="tr-rowbtn" onClick={() => setOpen(open === s.id ? null : s.id)} aria-expanded={open === s.id}>
                <td>{fmtDateTime(s.startedAt)}</td>
                {props.showUser ? <td>{s.userName}</td> : null}
                <td>{s.campaignName}</td>
                <td>{KIND_LABEL[s.personaKind]} · {s.personaName}</td>
                <td class="mono">{dur(s.durationSec)}</td>
                <td>
                  {s.score !== null
                    ? <span class={`chip ${s.passed ? 'chip-ok' : 'chip-bad'}`}>{s.score}{s.passed ? ' pass' : ''}</span>
                    : <span class="muted" style="font-size:13px">{s.note}</span>}
                </td>
                <td>
                  {canScore ? (
                    <button class="btn btn-sm" disabled={busy !== null} onClick={(e) => { e.stopPropagation(); score(s); }}>
                      {busy === s.id ? 'Scoring…' : 'Score'}
                    </button>
                  ) : null}
                </td>
              </tr>,
              open === s.id && s.scorecard ? (
                <tr><td colSpan={props.showUser ? 7 : 6} style="background:var(--white)"><ScorecardView card={s.scorecard} /></td></tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}
